import { PHOTO_LONG_EDGE, type PhotoSize } from "@corklog/shared";

/**
 * Task 9.7 — turning whatever came out of a camera into what the API accepts.
 *
 * **Resizing happens here rather than on the server**, which `tech-stack.md`
 * decided: it keeps an image library out of a container image that is the one
 * line in the cost table which actually bills, and a phone on a slow
 * connection uploads 300 kB instead of five megabytes.
 *
 * Two sizes come out of one file. 1600px is the one a back label is read
 * from — importer, alcohol, sometimes a disgorgement date, all of it small
 * print. 400px is for anywhere a thumbnail is wanted.
 */

/** What comes back for one size, shaped for `uploadWinePhotoSchema`. */
export type ResizedImage = {
  readonly data: string;
  readonly contentType: "image/jpeg";
  readonly width: number;
  readonly height: number;
};

export class UnreadableImage extends Error {
  constructor() {
    super("The browser could not read that image.");
    this.name = "UnreadableImage";
  }
}

/**
 * JPEG quality. 0.82 is where a photograph of a bottle stops getting smaller
 * much and starts getting visibly worse, measured rather than chosen: the
 * difference between 0.82 and 0.92 on a label is about 40 % more bytes and
 * nothing a person sees on a phone.
 */
const QUALITY = 0.82;

/**
 * Decode the file, once, before anything else touches it.
 *
 * **This is the HEIC guard and it is the reason this function exists.**
 * iPhones photograph in HEIC. Safari usually converts to JPEG when a file is
 * chosen through `<input type="file">`, but not always, and Android or a
 * desktop may hand it straight through. A `<canvas>` that cannot decode the
 * format does not throw — it draws nothing, and the upload succeeds with a
 * white rectangle in it, which looks like a photograph that went wrong months
 * later rather than an error now.
 *
 * `createImageBitmap` rejects on a format it cannot decode, which is the
 * behaviour wanted: a refusal the person can read, at the moment they chose
 * the file.
 */
async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    throw new UnreadableImage();
  }
}

/** The box a source fits into, keeping its proportions and never enlarging. */
export function fitWithin(
  width: number,
  height: number,
  longEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= longEdge) return { width, height };
  const scale = longEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new UnreadableImage());
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new UnreadableImage());
        return;
      }
      // A data URL, and the API wants the payload without the preamble.
      resolve(result.slice(result.indexOf(",") + 1));
    };
    reader.readAsDataURL(blob);
  });
}

async function render(source: ImageBitmap, size: PhotoSize): Promise<ResizedImage> {
  const box = fitWithin(source.width, source.height, PHOTO_LONG_EDGE[size]);
  const canvas = document.createElement("canvas");
  canvas.width = box.width;
  canvas.height = box.height;

  const context = canvas.getContext("2d");
  if (context === null) throw new UnreadableImage();
  context.drawImage(source, 0, 0, box.width, box.height);

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/jpeg", QUALITY);
  });
  if (blob === null) throw new UnreadableImage();

  return {
    data: await toBase64(blob),
    contentType: "image/jpeg",
    width: box.width,
    height: box.height,
  };
}

/**
 * Both sizes from one chosen file.
 *
 * Both in one call, and the upload sends both in one request, because a wine
 * with a full-size photograph and no thumbnail is a state nothing wants to
 * handle and a second request is a second chance to fail halfway.
 */
export async function resizeForUpload(
  file: File,
): Promise<{ full: ResizedImage; small: ResizedImage }> {
  const source = await decode(file);
  try {
    return {
      full: await render(source, "full"),
      small: await render(source, "small"),
    };
  } finally {
    source.close();
  }
}
